import { Progress } from "@/components/ui/progress"
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react"

interface ImportProgressProps {
  numFinished: number
  skippedPosts: Array<{ title: string; reason?: string }>
  erroredPosts: Array<{ title: string; reason?: string }>
  postsRemaining: number
}

export function ImportProgress({
  numFinished,
  skippedPosts,
  erroredPosts,
  postsRemaining,
}: ImportProgressProps) {
  const totalPosts =
    numFinished + skippedPosts.length + erroredPosts.length + postsRemaining
  const progressPercentage =
    totalPosts > 0
      ? ((numFinished + skippedPosts.length + erroredPosts.length) /
          totalPosts) *
        100
      : 0

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-base font-medium">Import progress</h3>

        <div className="space-y-2">
          <span className="text-sm font-medium">Importing posts...</span>

          <div className="flex items-center w-3/4">
            <div className="flex-1">
              <Progress value={progressPercentage} className="h-2" />
            </div>
            <span className="text-sm font-medium ml-2">
              {Math.round(progressPercentage)}%
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {numFinished > 0 && (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="text-sm">
              {numFinished} post{numFinished !== 1 ? "s" : ""} imported
              successfully
            </span>
          </div>
        )}

        {skippedPosts.length > 0 && (
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
            <div className="space-y-1">
              <span className="text-sm">
                {skippedPosts.length} post{skippedPosts.length !== 1 ? "s" : ""}{" "}
                skipped (already{" "}
                {skippedPosts.length === 1 ? "exists" : "exist"})
              </span>
              {skippedPosts.length <= 3 && (
                <ul className="text-xs text-muted-foreground pl-4 list-disc">
                  {skippedPosts.map((post, i) => (
                    <li key={i}>
                      {post.title}
                      {post.reason && (
                        <span className="italic ml-1">({post.reason})</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {erroredPosts.length > 0 && (
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 text-destructive mt-0.5" />
            <div className="space-y-1">
              <span className="text-sm">
                {erroredPosts.length} post{erroredPosts.length !== 1 ? "s" : ""}{" "}
                failed to import
              </span>
              {erroredPosts.length <= 3 && (
                <ul className="text-xs text-muted-foreground pl-4 list-disc">
                  {erroredPosts.map((post, i) => (
                    <li key={i}>
                      {post.title}
                      {post.reason && (
                        <span className="italic ml-1">({post.reason})</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {postsRemaining > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {postsRemaining} post{postsRemaining !== 1 ? "s" : ""} remaining
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
